import { correctedDraftEmailType } from '../../EmailAiModes/contactStage';

const FOOTER_MARKER = '<!-- FOOTER -->';

// The server would block these again no matter what the user approves.
const NOT_RESENDABLE_REASONS = new Set(['crm_blocked', 'email_invalid_final']);

const PLACEHOLDER_BODIES = [
  'email-inhalt konnte nicht generiert werden',
  'email content could not be generated',
];

const firstAddress = (value) => {
  if (Array.isArray(value)) return firstAddress(value[0]);
  if (typeof value !== 'string') return '';
  const match = value.match(/[^\s<>,;"]+@[^\s<>,;"]+\.[^\s<>,;"]+/);
  return match ? match[0].trim() : '';
};

const decodeEntities = (text) => {
  if (typeof document !== 'undefined') {
    const el = document.createElement('textarea');
    el.innerHTML = text;
    return el.value;
  }
  return text
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&');
};

export function htmlToPlainText(html) {
  if (!html || typeof html !== 'string') return '';
  const text = html
    .replace(/<(style|script|head)[\s\S]*?<\/\1>/gi, '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|h[1-6]|tr|blockquote)>/gi, '\n\n')
    .replace(/<[^>]+>/g, '');
  return decodeEntities(text)
    .replace(/\u00a0/g, ' ')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * The editable body of a parked mail: the HTML up to the signature marker,
 * as plain text, so re-sending through a mode doesn't append a second footer.
 */
export function draftBodyText(draft) {
  const html = typeof draft?.content === 'string' ? draft.content : '';
  if (html) {
    const cut = html.indexOf(FOOTER_MARKER);
    const text = htmlToPlainText(cut >= 0 ? html.slice(0, cut) : html);
    if (text) return text;
  }
  return typeof draft?.originalEmailContent === 'string' ? draft.originalEmailContent.trim() : '';
}

export function draftRecipient(draft) {
  return firstAddress(draft?.originalTo) || firstAddress(draft?.to) || firstAddress(draft?.recipientEmail);
}

export function draftSender(draft) {
  return firstAddress(draft?.accountEmail) || firstAddress(draft?.from) || firstAddress(draft?.senderEmail);
}

export function resendBlockReason(draft) {
  if (!draft) return 'missing';
  if (NOT_RESENDABLE_REASONS.has(draft.reason)) return 'blocked_reason';
  if (!draft.leadId) return 'no_contact';
  if (!draftRecipient(draft)) return 'no_recipient';
  const body = draftBodyText(draft);
  if (!body || PLACEHOLDER_BODIES.some((p) => body.toLowerCase().startsWith(p))) return 'no_content';
  return null;
}

export const isResendableDraft = (draft) => resendBlockReason(draft) === null;

/**
 * Everything a mode needs to show the parked mail again as it was meant to go
 * out. `prepared` is the contact's pending prepared draft, if any — it carries
 * the real step for mails the duplicate-intro guard parked as "intro".
 */
export function buildResendPreset(draft, prepared = null) {
  const subject = draft.subject || '';
  const emailType = correctedDraftEmailType({
    requested: draft.emailType && draft.emailType !== 'unknown' ? draft.emailType : '',
    prepared,
    subject,
  });
  const cc = (Array.isArray(draft.cc) ? draft.cc : [draft.cc])
    .map(firstAddress)
    .filter(Boolean);
  const attachments = (Array.isArray(draft.attachments) ? draft.attachments : [])
    .filter((a) => a && a.storagePath)
    .map((a, i) => ({
      tempId: `sg_${draft.id}_${i}`,
      filename: a.filename,
      storagePath: a.storagePath,
      contentType: a.contentType,
      size: a.size,
    }));
  return {
    draftId: draft.id,
    subject,
    body: draftBodyText(draft),
    recipient: draftRecipient(draft),
    cc,
    senderEmail: draftSender(draft),
    emailType,
    topicName: draft.topicName || (emailType === prepared?.emailType ? prepared?.topicName || '' : ''),
    attachments,
  };
}

const draftTime = (draft) => {
  const ts = draft?.timestamp;
  if (ts && typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof ts === 'number') return ts;
  const parsed = Date.parse(draft?.date || '');
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * One parked mail per contact (the newest), skipping what can't be re-sent.
 * Bulk mode walks contacts, so two drafts for the same lead can't both ride.
 */
export function pickBulkResendDrafts(drafts) {
  const byLead = new Map();
  (drafts || []).forEach((draft) => {
    if (!isResendableDraft(draft)) return;
    const current = byLead.get(draft.leadId);
    if (!current || draftTime(draft) > draftTime(current)) byLead.set(draft.leadId, draft);
  });
  return [...byLead.values()].sort((a, b) => draftTime(b) - draftTime(a));
}
